import {
  GoodThing,
  DashboardMetrics
} from '../types/good-things';
import { dashboardService } from './firebase/DashboardService';
import { sopService } from './firebase/SOPService';
import { employeeNamesService } from './employeeNames';
import { syncTimeService } from './syncTimeService';
import { firestoreWrapper } from './firestoreWrapper';
import { db } from '../config/firebase';
import { PMT2SyncStatus } from '../types/pmt2Types';
import { 
  DocumentData, 
  QueryDocumentSnapshot,
  Query,
  DocumentSnapshot,
  QueryConstraint
} from 'firebase/firestore';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';

// Cache configuration
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes (reduced from 24 hours)
const CACHE_STALE_TTL = 60 * 60 * 1000; // 1 hour (stale data threshold)
const GOOD_THINGS_CACHE_KEY = 'goodThingsCache';
const APP_HUB_METRICS_CACHE_KEY = 'appHubMetricsCache';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Load cache from localStorage
const loadCache = <T>(key: string): CacheEntry<T> | null => {
  const cached = localStorage.getItem(key);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Store the timestamp of the last manual refresh
let lastManualRefreshTimestamp = 0;

// Function to set the manual refresh timestamp
export const setManualRefreshTimestamp = () => {
  lastManualRefreshTimestamp = Date.now();
};

// Last sync timestamp cache to avoid Firestore queries
let lastSyncTimestampCache: { timestamp: number, lastChecked: number } | null = null;
const SYNC_TIMESTAMP_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Track the last time we checked for a sync to avoid excessive Firestore reads
let lastSyncCheckTime = 0;

/**
 * Validate cache against last sync timestamp
 * This implementation is optimized to minimize Firestore reads while still
 * ensuring data freshness by checking for new syncs periodically
 */
const validateCache = async <T>(cachedData: CacheEntry<T> | null, cacheKey: string = ''): Promise<CacheEntry<T> | null> => {
  if (!cachedData) return null;
  
  // If the cache is older than the last manual refresh, invalidate it
  if (lastManualRefreshTimestamp > 0 && cachedData.timestamp < lastManualRefreshTimestamp) {
    
    return null; // Cache is invalid
  }
  
  // Check if we're due for a sync check based on the 1-hour schedule
  const now = Date.now();
  const lastKnownSync = syncTimeService.getLastKnownSync();
  const oneHourMs = 60 * 60 * 1000; // 1 hour in milliseconds
  
  // Only check Firestore if we're due for a sync and haven't checked recently
  if ((now - lastKnownSync > oneHourMs) && (now - lastSyncCheckTime > 15 * 60 * 1000)) {
    try {
      // Update the last check time immediately to prevent multiple checks
      lastSyncCheckTime = now;
      syncTimeService.storeLastSyncCheckTime(now);
      
      
      
      // Make a single Firestore read with rate limiting
      const { firestoreWrapper } = await import('./firestoreWrapper');
      const syncDoc = await firestoreWrapper.getDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('goodThingsSync'),
          'latest'
        )
      );
      
      if (syncDoc.exists()) {
        const lastSync = syncDoc.data().lastSync;
        if (lastSync) {
          const lastSyncTimestamp = lastSync.toMillis ? lastSync.toMillis() : new Date(lastSync).getTime();
          
          // Update our last known sync time
          syncTimeService.storeLastKnownSync(lastSyncTimestamp);
          
          // If cache is older than the last sync, invalidate it
          if (cachedData.timestamp < lastSyncTimestamp) {
            
            return null; // Cache is invalid
          }
        }
      }
    } catch (error) {
      
      // On error, return the cached data anyway
    }
  }
  
  return cachedData; // Cache is still valid
};

// Save cache to localStorage
const saveCache = <T>(key: string, data: T) => {
  try {
    const cacheData: CacheEntry<T> = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (e) {
    // Silently fail - no need to log cache errors
  }
};

interface QueryOptions {
  page?: number;
  limit?: number;
  category?: string;
  dateFrom?: string;
  dateTo?: string;
  person?: string | string[];
}

interface QueryResult<T> {
  items: T[];
  total: number;
  hasMore: boolean;
}

// Utility function to parse any date format
const parseAnyDateFormat = (dateStr: string): Date => {
  if (!dateStr) return new Date(0);
  
  // Try direct parsing first
  let date = new Date(dateStr);
  
  // If invalid or year is wrong, try MM/DD/YY format
  if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      // Assume 20xx for two-digit years
      date = new Date(`20${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    }
  }
  
  return date;
};

// Create and export the metricsCache object
export const metricsCache = {
  // Cache for sorted entries
  sortedEntriesCache: new Map<string, GoodThing[]>(),

  async getGoodThingsEntries(options: QueryOptions = {}): Promise<{ items: GoodThing[]; total: number }> {
    try {
      const {
        page = 1,
        limit,
        category,
        dateFrom,
        dateTo,
        person
      } = options;

      const cacheKey = `${GOOD_THINGS_CACHE_KEY}_${JSON.stringify(options)}`;
      const cached = await validateCache(loadCache<QueryResult<GoodThing>>(cacheKey), cacheKey);
      const now = Date.now();

      // Return cached data if fresh and valid against last sync
      if (cached && now - cached.timestamp < CACHE_TTL) {
        return cached.data;
      }

      // Build query
      const constraints: QueryConstraint[] = [firestoreWrapper.orderBy('date', 'desc')];

      if (category) {
        constraints.push(firestoreWrapper.where('category', '==', category));
      }
      if (dateFrom) {
        constraints.push(firestoreWrapper.where('date', '>=', dateFrom));
      }
      if (dateTo) {
        constraints.push(firestoreWrapper.where('date', '<=', dateTo));
      }
      if (person) {
        if (Array.isArray(person)) {
          // For array of names, we need to use a different approach
          // We'll query for each name separately and merge the results
          const allResults: GoodThing[] = [];
          const uniqueIds = new Set<string>();
          
          for (const name of person) {
            // For each name, check both with and without period
            const namesToCheck = [name];
            if (!name.endsWith('.')) {
              namesToCheck.push(`${name}.`);
            }
            
            for (const nameToCheck of namesToCheck) {
              const nameConstraints = [...constraints, firestoreWrapper.where('recipients', 'array-contains', nameToCheck)];
              if (limit) {
                nameConstraints.push(firestoreWrapper.limit(limit * page));
              }
              
              const nameQuery = firestoreWrapper.query(
                firestoreWrapper.collection('goodThings'), 
                ...nameConstraints
              );
              const nameSnapshot = await firestoreWrapper.getDocs(nameQuery);
              
              // Add unique results to the combined results
              nameSnapshot.docs.forEach((doc: any) => {
                if (!uniqueIds.has(doc.id)) {
                  uniqueIds.add(doc.id);
                  const data = doc.data();
                  allResults.push({
                    id: doc.id,
                    date: data.date,
                    submitter: data.submitter,
                    category: data.category,
                    recipients: data.recipients || [],
                    note: data.note,
                    richNote: data.richNote || null, // Explicitly include rich text data
                    timestamp: data.timestamp?.toDate() || new Date()
                  } as GoodThing);
                }
              });
            }
          }
          
          // Sort the combined results by date
          allResults.sort((a, b) => {
            // Convert MM/DD/YY to YYYY-MM-DD for proper comparison
            const [aMonth, aDay, aYear] = a.date.split('/');
            const [bMonth, bDay, bYear] = b.date.split('/');
            const aDate = `20${aYear}-${aMonth.padStart(2, '0')}-${aDay.padStart(2, '0')}`;
            const bDate = `20${bYear}-${bMonth.padStart(2, '0')}-${bDay.padStart(2, '0')}`;
            return bDate.localeCompare(aDate);
          });
          
          // Apply pagination to the combined results
          const paginatedResults = allResults.slice(0, limit ? limit * page : undefined);
          
          const result = {
            items: paginatedResults,
            total: allResults.length,
            hasMore: limit ? paginatedResults.length === limit * page : false
          };
          
          // Save to cache
          saveCache(cacheKey, result);
          return result;
        } else {
          // For a single name, we need to check both with and without period
          // We'll query for each variant separately and merge the results
          const allResults: GoodThing[] = [];
          const uniqueIds = new Set<string>();
          
          // Prepare name variants to check
          const namesToCheck = [person];
          if (!person.endsWith('.')) {
            namesToCheck.push(`${person}.`);
          }
          
          // Also check email if available
          const email = await employeeNamesService.getEmailFromDisplayName(person);
          if (email) {
            namesToCheck.push(email);
          }
          
          for (const nameToCheck of namesToCheck) {
            const nameConstraints = [...constraints, firestoreWrapper.where('recipients', 'array-contains', nameToCheck)];
            if (limit) {
              nameConstraints.push(firestoreWrapper.limit(limit * page));
            }
            
            const nameQuery = firestoreWrapper.query(
              firestoreWrapper.collection('goodThings'), 
              ...nameConstraints
            );
            const nameSnapshot = await firestoreWrapper.getDocs(nameQuery);
            
            // Add unique results to the combined results
            nameSnapshot.docs.forEach((doc: any) => {
              if (!uniqueIds.has(doc.id)) {
                uniqueIds.add(doc.id);
                const data = doc.data();
                allResults.push({
                  id: doc.id,
                  date: data.date,
                  submitter: data.submitter,
                  category: data.category,
                  recipients: data.recipients || [],
                  note: data.note,
                  richNote: data.richNote || null, // Explicitly include rich text data
                  timestamp: data.timestamp?.toDate() || new Date()
                } as GoodThing);
              }
            });
          }
          
          // Sort the combined results by date
          allResults.sort((a, b) => {
            // Convert MM/DD/YY to YYYY-MM-DD for proper comparison
            const [aMonth, aDay, aYear] = a.date.split('/');
            const [bMonth, bDay, bYear] = b.date.split('/');
            const aDate = `20${aYear}-${aMonth.padStart(2, '0')}-${aDay.padStart(2, '0')}`;
            const bDate = `20${bYear}-${bMonth.padStart(2, '0')}-${bDay.padStart(2, '0')}`;
            return bDate.localeCompare(aDate);
          });
          
          // Apply pagination to the combined results
          const paginatedResults = allResults.slice(0, limit ? limit * page : undefined);
          
          const result = {
            items: paginatedResults,
            total: allResults.length,
            hasMore: limit ? paginatedResults.length === limit * page : false
          };
          
          // Save to cache
          saveCache(cacheKey, result);
          return result;
        }
      }

      // If no person filter, use a simple query
      if (limit) {
        constraints.push(firestoreWrapper.limit(limit * page));
      }

      const baseQuery = firestoreWrapper.query(
        firestoreWrapper.collection('goodThings'), 
        ...constraints
      );
      const snapshot = await firestoreWrapper.getDocs(baseQuery);

      const items = snapshot.docs.map((doc: any) => {
        const data = doc.data();
        return {
          id: doc.id,
          date: data.date,
          submitter: data.submitter,
          category: data.category,
          recipients: data.recipients || [],
          note: data.note,
          richNote: data.richNote || null, // Explicitly include rich text data
          timestamp: data.timestamp?.toDate() || new Date()
        };
      }) as GoodThing[];

      const result = {
        items,
        total: snapshot.size,
        hasMore: limit ? items.length === limit * page : false
      };

      // Save to cache
      saveCache(cacheKey, result);
      return result;

    } catch (error) {
      
      return this.getStaleCache(options);
    }
  },

  async getStaleCache(options: QueryOptions): Promise<{ items: GoodThing[]; total: number }> {
    const cacheKey = `${GOOD_THINGS_CACHE_KEY}_${JSON.stringify(options)}`;
    const cached = await validateCache(loadCache<QueryResult<GoodThing>>(cacheKey), cacheKey);
    const now = Date.now();

    if (cached && now - cached.timestamp < CACHE_STALE_TTL) {
      
      return cached.data;
    }

    return { items: [], total: 0 };
  },

  async getDashboardMetrics(): Promise<DashboardMetrics> {
    const cacheKey = `${GOOD_THINGS_CACHE_KEY}_metrics`;
    const cached = await validateCache(loadCache<DashboardMetrics>(cacheKey), cacheKey);
    const now = Date.now();

    if (cached && now - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    try {
      // Get all entries with a single query
      const allEntries = await this.getGoodThingsEntries();
      
      // Filter for weekly entries
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7)); // Go back to Monday
      weekStart.setHours(0, 0, 0, 0);
      
      const weeklyEntries = allEntries.items.filter(entry => {
        const entryDate = parseAnyDateFormat(entry.date);
        return entryDate >= weekStart && !isNaN(entryDate.getTime());
      });
      
      // Get most recent entry
      const recentEntries = allEntries.items.slice(0, 1);

      const metrics: DashboardMetrics = {
        totalGoodThings: allEntries.items.length,
        uniqueContributors: new Set(weeklyEntries.map(entry => entry.submitter)).size,
        currentWeek: weeklyEntries.length,
        topCategories: [],
        categoryDistribution: [],
        timeline: [],
        recentUserGoodThings: recentEntries
      };

      saveCache(cacheKey, metrics);
      return metrics;
    } catch (error) {
      
      return cached?.data || {
        totalGoodThings: 0,
        uniqueContributors: 0,
        currentWeek: 0,
        topCategories: [],
        categoryDistribution: [],
        timeline: [],
        recentUserGoodThings: []
      };
    }
  },

  async getGoodThingsAppHubMetrics(): Promise<{ totalEntries: number; uniqueContributors: number; currentWeek: number; lastActivity: string }> {
    try {
      // Get all metrics directly from the sync metadata
      const syncDoc = await firestoreWrapper.getDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('goodThingsSync'),
          'latest'
        )
      );
      
      if (!syncDoc.exists()) {
        return {
          totalEntries: 0,
          uniqueContributors: 0,
          currentWeek: 0,
          lastActivity: '-'
        };
      }
      
      const syncData = syncDoc.data();
      
      // Format the latest entry date
      let lastActivity = '-';
      if (syncData.latestEntryDate) {
        const date = parseAnyDateFormat(syncData.latestEntryDate);
        lastActivity = date.toLocaleDateString('en-US', { 
          month: 'numeric', 
          day: 'numeric', 
          year: 'numeric' 
        });
      }
      
      // Check if we have all the metrics we need from the sync metadata
      if (syncData.uniqueContributorsThisWeek !== undefined &&
          syncData.weeklyEntryCount !== undefined) {
        // Use the actual total from spreadsheet instead of processedCount
        const actualTotal = syncData.totalEntriesInSpreadsheet || syncData.processedCount || 0;
        return {
          totalEntries: actualTotal,
          uniqueContributors: syncData.uniqueContributorsThisWeek || 0,
          currentWeek: syncData.weeklyEntryCount || 0,
          lastActivity: lastActivity
        };
      }
      
      // If the sync metadata doesn't have all the metrics we need, fall back to the old implementation
      
      
      // Get the dashboard metrics for contributors and weekly count
      const metrics = await this.getDashboardMetrics();
      
      // If we don't have the latest entry date from sync metadata, query for it
      if (!syncData.latestEntryDate) {
        try {
          // Query for the most recent entry by timestamp
          const recentQuery = firestoreWrapper.query(
            firestoreWrapper.collection('goodThings'),
            firestoreWrapper.orderBy('timestamp', 'desc'), // Use timestamp instead of date for proper ordering
            firestoreWrapper.limit(1)
          );
          const recentSnapshot = await firestoreWrapper.getDocs(recentQuery);
          
          if (!recentSnapshot.empty) {
            const mostRecentEntry = recentSnapshot.docs[0].data() as GoodThing;
            if (mostRecentEntry.date) {
              const date = parseAnyDateFormat(mostRecentEntry.date);
              lastActivity = date.toLocaleDateString('en-US', { 
                month: 'numeric', 
                day: 'numeric', 
                year: 'numeric' 
              });
            }
          }
        } catch (error) {
          
          // No fallback date - just use the dash if we can't get the date
        }
      }
      
      // Use the actual total from spreadsheet instead of processedCount
      const actualTotal = syncData.totalEntriesInSpreadsheet || syncData.processedCount || 0;
      return {
        totalEntries: actualTotal,
        uniqueContributors: metrics.uniqueContributors,
        currentWeek: metrics.currentWeek,
        lastActivity: lastActivity
      };
    } catch (error) {
      
      return {
        totalEntries: 0,
        uniqueContributors: 0,
        currentWeek: 0,
        lastActivity: '-'
      };
    }
  },

  async getLMSAppHubMetrics(): Promise<{ totalCourses: number; completedCourses: number; lastSync: string }> {
    try {
      // Get cached LMS metrics from appHubMetrics collection
      const lmsDoc = await firestoreWrapper.getDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('appHubMetrics'),
          'lms'
        )
      );
      
      if (!lmsDoc.exists()) {
        return {
          totalCourses: 0,
          completedCourses: 0,
          lastSync: '-'
        };
      }
      
      const data = lmsDoc.data();
      
      // Format the last updated time
      let lastSync = '-';
      if (data.lastUpdated) {
        let date: Date;
        if (typeof data.lastUpdated === 'string') {
          date = new Date(data.lastUpdated);
        } else if (data.lastUpdated.toDate) {
          date = data.lastUpdated.toDate();
        } else {
          date = new Date(data.lastUpdated);
        }
        lastSync = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
      }
      
      return {
        totalCourses: data.totalCourses || 0,
        completedCourses: data.completedCourses || 0,
        lastSync: lastSync
      };
    } catch (error) {
      return {
        totalCourses: 0,
        completedCourses: 0,
        lastSync: '-'
      };
    }
  },

  async getSOPAppHubMetrics(): Promise<{ totalSOPs: number; activeSOPs: number; lastSync: string }> {
    try {
      // Get cached SOP metrics from appHubMetrics collection
      const sopDoc = await firestoreWrapper.getDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('appHubMetrics'),
          'sop'
        )
      );
      
      if (!sopDoc.exists()) {
        return {
          totalSOPs: 0,
          activeSOPs: 0,
          lastSync: '-'
        };
      }
      
      const data = sopDoc.data();
      
      // Format the last updated time
      let lastSync = '-';
      if (data.lastUpdated) {
        let date: Date;
        if (typeof data.lastUpdated === 'string') {
          date = new Date(data.lastUpdated);
        } else if (data.lastUpdated.toDate) {
          date = data.lastUpdated.toDate();
        } else {
          date = new Date(data.lastUpdated);
        }
        lastSync = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
      }
      
      return {
        totalSOPs: data.totalSOPs || 0,
        activeSOPs: data.activeSOPs || 0,
        lastSync: lastSync
      };
    } catch (error) {
      return {
        totalSOPs: 0,
        activeSOPs: 0,
        lastSync: '-'
      };
    }
  },

  async getPLMSAppHubMetrics(workbookId?: string): Promise<{ doneItems: number; itemsRemaining: number; percentDone: number; itemsDoneLastWeek: number; lastActivity: string }> {
    try {
      // Get cached PLMS metrics from appHubMetrics collection
      const plmsDoc = await firestoreWrapper.getDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('appHubMetrics'),
          'plms'
        )
      );
      
      if (!plmsDoc.exists()) {
        return {
          doneItems: 0,
          itemsRemaining: 0,
          percentDone: 0,
          itemsDoneLastWeek: 0,
          lastActivity: '-'
        };
      }
      
      const data = plmsDoc.data();
      
      // Format the last activity
      let lastActivity = '-';
      if (data.lastUpdated) {
        let date: Date;
        if (typeof data.lastUpdated === 'string') {
          date = new Date(data.lastUpdated);
        } else if (data.lastUpdated.toDate) {
          date = data.lastUpdated.toDate();
        } else {
          date = new Date(data.lastUpdated);
        }
        lastActivity = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
      }
      
      return {
        doneItems: data.additionalData?.doneItems || 0,
        itemsRemaining: data.additionalData?.itemsRemaining || 0,
        percentDone: data.additionalData?.percentDone || 0,
        itemsDoneLastWeek: data.additionalData?.itemsDoneLastWeek || 0,
        lastActivity: lastActivity
      };
    } catch (error) {
      return {
        doneItems: 0,
        itemsRemaining: 0,
        percentDone: 0,
        itemsDoneLastWeek: 0,
        lastActivity: '-'
      };
    }
  },

  async getPMT2AppHubMetrics(): Promise<{ totalSyncs: number; completedSyncs: number; lastSync: string }> {
    try {
      // Get cached PMT2 metrics from appHubMetrics collection
      const pmt2Doc = await firestoreWrapper.getDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('appHubMetrics'),
          'pmt2'
        )
      );
      
      if (!pmt2Doc.exists()) {
        return {
          totalSyncs: 0,
          completedSyncs: 0,
          lastSync: '-'
        };
      }
      
      const data = pmt2Doc.data();
      
      // Format the last sync time
      let lastSync = '-';
      if (data.additionalData?.lastSyncTime) {
        let date: Date;
        if (typeof data.additionalData.lastSyncTime === 'string') {
          date = new Date(data.additionalData.lastSyncTime);
        } else if (data.additionalData.lastSyncTime.toDate) {
          date = data.additionalData.lastSyncTime.toDate();
        } else {
          date = new Date(data.additionalData.lastSyncTime);
        }
        lastSync = date.toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric'
        });
      } else if (data.lastUpdated) {
        let date: Date;
        if (typeof data.lastUpdated === 'string') {
          date = new Date(data.lastUpdated);
        } else if (data.lastUpdated.toDate) {
          date = data.lastUpdated.toDate();
        } else {
          date = new Date(data.lastUpdated);
        }
        lastSync = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
      }
      
      return {
        totalSyncs: data.additionalData?.totalChanges || data.totalCount || 0,
        completedSyncs: data.additionalData?.finalTotalProducts || data.recentCount || 0,
        lastSync: lastSync
      };
    } catch (error) {
      return {
        totalSyncs: 0,
        completedSyncs: 0,
        lastSync: '-'
      };
    }
  },

  async getGlossaryAppHubMetrics(): Promise<{ totalTerms: number; activeTerms: number; lastSync: string }> {
    try {
      // Get cached Glossary metrics from appHubMetrics collection
      const glossaryDoc = await firestoreWrapper.getDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('appHubMetrics'),
          'glossary'
        )
      );
      
      if (!glossaryDoc.exists()) {
        return {
          totalTerms: 0,
          activeTerms: 0,
          lastSync: '-'
        };
      }
      
      const data = glossaryDoc.data();
      
      // Format the last updated time
      let lastSync = '-';
      if (data.lastUpdated) {
        let date: Date;
        if (typeof data.lastUpdated === 'string') {
          date = new Date(data.lastUpdated);
        } else if (data.lastUpdated.toDate) {
          date = data.lastUpdated.toDate();
        } else {
          date = new Date(data.lastUpdated);
        }
        lastSync = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
      }
      
      return {
        totalTerms: data.totalTerms || 0,
        activeTerms: data.activeTerms || 0,
        lastSync: lastSync
      };
    } catch (error) {
      return {
        totalTerms: 0,
        activeTerms: 0,
        lastSync: '-'
      };
    }
  },
  async getDayAwayAppHubMetrics(): Promise<{ totalRequests: number; pendingRequests: number; lastSync: string }> {
    try {
      // Get cached Day Away metrics from appHubMetrics collection
      const dayAwayDoc = await firestoreWrapper.getDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('appHubMetrics'),
          'dayaway'
        )
      );
      
      if (!dayAwayDoc.exists()) {
        return {
          totalRequests: 0,
          pendingRequests: 0,
          lastSync: '-'
        };
      }
      
      const data = dayAwayDoc.data();
      
      // Format the last updated time
      let lastSync = '-';
      if (data.lastUpdated) {
        let date: Date;
        if (typeof data.lastUpdated === 'string') {
          date = new Date(data.lastUpdated);
        } else if (data.lastUpdated.toDate) {
          date = data.lastUpdated.toDate();
        } else {
          date = new Date(data.lastUpdated);
        }
        lastSync = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
      }
      
      return {
        totalRequests: data.totalRequests || 0,
        pendingRequests: data.pendingRequests || 0,
        lastSync: lastSync
      };
    } catch (error) {
      return {
        totalRequests: 0,
        pendingRequests: 0,
        lastSync: '-'
      };
    }
  },

  async getIdeogramAppHubMetrics(): Promise<{ totalImages: number; generatedImages: number; lastSync: string }> {
    try {
      // Get cached Ideogram metrics from appHubMetrics collection
      const ideogramDoc = await firestoreWrapper.getDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('appHubMetrics'),
          'ideogram'
        )
      );
      
      if (!ideogramDoc.exists()) {
        return {
          totalImages: 0,
          generatedImages: 0,
          lastSync: '-'
        };
      }
      
      const data = ideogramDoc.data();
      
      // Format the last updated time
      let lastSync = '-';
      if (data.lastUpdated) {
        let date: Date;
        if (typeof data.lastUpdated === 'string') {
          date = new Date(data.lastUpdated);
        } else if (data.lastUpdated.toDate) {
          date = data.lastUpdated.toDate();
        } else {
          date = new Date(data.lastUpdated);
        }
        lastSync = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
      }
      
      return {
        totalImages: data.totalImages || 0,
        generatedImages: data.generatedImages || 0,
        lastSync: lastSync
      };
    } catch (error) {
      return {
        totalImages: 0,
        generatedImages: 0,
        lastSync: '-'
      };
    }
  },

  // Get cached App Hub metrics immediately from localStorage (no Firestore)
  getCachedAppHubMetrics(): {
    lms?: { totalCourses: number; completedCourses: number; lastSync: string };
    sop?: { totalSOPs: number; activeSOPs: number; departments: number; authors: number; latestSOP: string; lastSync: string };
    dayaway?: { totalRequests: number; pendingRequests: number; offToday: number; offTomorrow: number; daysThisYear: number; lastSync: string };
    pmt2?: { totalSyncs: number; completedSyncs: number; totalSpreadsheets: number; totalProducts: number; companiesCount: number; lastSync: string };
    glossary?: { totalTerms: number; activeTerms: number; totalCategories: number; lastSyncDate: string; lastSync: string };
    ideogram?: { totalImages: number; generatedImages: number; lastActivity: string; lastSync: string };
    cached?: boolean;
    cacheAge?: number;
  } {
    try {
      const cached = loadCache<any>(APP_HUB_METRICS_CACHE_KEY);
      if (cached && cached.data) {
        const cacheAge = Date.now() - cached.timestamp;
        return {
          ...cached.data,
          cached: true,
          cacheAge: Math.floor(cacheAge / 1000) // age in seconds
        };
      }
    } catch (e) {
      // Silently fail - return empty object
    }
    return { cached: false };
  },

  // Batch loading function for all App Hub metrics - PERFORMANCE OPTIMIZATION
  async getAllAppHubMetrics(): Promise<{
    lms?: { totalCourses: number; completedCourses: number; lastSync: string };
    sop?: { totalSOPs: number; activeSOPs: number; departments: number; authors: number; latestSOP: string; lastSync: string };
    dayaway?: { totalRequests: number; pendingRequests: number; offToday: number; offTomorrow: number; daysThisYear: number; lastSync: string };
    pmt2?: { totalSyncs: number; completedSyncs: number; totalSpreadsheets: number; totalProducts: number; companiesCount: number; lastSync: string };
    glossary?: { totalTerms: number; activeTerms: number; totalCategories: number; lastSyncDate: string; lastSync: string };
    ideogram?: { totalImages: number; generatedImages: number; lastActivity: string; lastSync: string };
  }> {
    try {
      // Single Firestore query to get all cached app metrics
      const snapshot = await firestoreWrapper.getDocs(
        firestoreWrapper.collection('appHubMetrics')
      );
      
      if (snapshot.empty) {
        return {};
      }

      const allMetrics: any = {};

      // Helper function to parse date safely
      const parseDate = (dateValue: any) => {
        if (typeof dateValue === 'string') {
          return new Date(dateValue);
        } else if (dateValue && dateValue.toDate) {
          return dateValue.toDate();
        } else {
          return new Date(dateValue);
        }
      };

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const appKey = doc.id;

        try {
          // Format last updated date
          let lastSyncFormatted = '-';
          if (data.lastUpdated) {
            const lastUpdatedDate = parseDate(data.lastUpdated);
            lastSyncFormatted = lastUpdatedDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit'
            });
          }

          // Format data according to each app's expected structure
          switch (appKey) {
            case 'lms':
              allMetrics.lms = {
                totalCourses: data.additionalData?.totalCourses || data.totalCount || 0,
                completedCourses: data.additionalData?.coursesCompleted || data.recentCount || 0,
                lastSync: lastSyncFormatted
              };
              break;
            
            case 'sop':
              allMetrics.sop = {
                totalSOPs: data.totalCount || 0,
                activeSOPs: data.recentCount || 0,
                departments: data.additionalData?.departmentCount || 0,
                authors: data.additionalData?.authorCount || 0,
                latestSOP: data.additionalData?.latestEntryDate || '-',
                lastSync: lastSyncFormatted
              };
              break;

            case 'dayaway':
              allMetrics.dayaway = {
                totalRequests: data.totalCount || 0,
                pendingRequests: data.recentCount || 0,
                offToday: data.additionalData?.offToday || 0,
                offTomorrow: data.additionalData?.offTomorrow || 0,
                daysThisYear: data.additionalData?.daysThisYear || 0,
                lastSync: lastSyncFormatted
              };
              break;

            case 'pmt2':
              allMetrics.pmt2 = {
                totalSyncs: data.additionalData?.totalChanges || data.totalCount || 0,
                completedSyncs: data.additionalData?.finalTotalProducts || data.recentCount || 0,
                totalSpreadsheets: data.additionalData?.processedSpreadsheets || data.additionalData?.companiesCount || 0,
                totalProducts: data.additionalData?.finalTotalProducts || 0,
                companiesCount: data.additionalData?.processedSpreadsheets || data.additionalData?.companiesCount || 0,
                lastSync: lastSyncFormatted
              };
              break;

            case 'glossary':
              allMetrics.glossary = {
                totalTerms: data.totalCount || 0,
                activeTerms: data.recentCount || 0,
                totalCategories: data.additionalData?.totalCategories || 0,
                lastSyncDate: lastSyncFormatted,
                lastSync: lastSyncFormatted
              };
              break;

            case 'ideogram':
              allMetrics.ideogram = {
                totalImages: data.totalCount || 0,
                generatedImages: data.recentCount || 0,
                lastActivity: lastSyncFormatted,
                lastSync: lastSyncFormatted
              };
              break;
          }
        } catch (error) {
          // Silently handle individual app metric processing errors
        }
      });

      // Save to localStorage cache for instant loading next time
      saveCache(APP_HUB_METRICS_CACHE_KEY, allMetrics);
      
      return allMetrics;
    } catch (error) {
      return {};
    }
  },

  async getAIDevQuestAppHubMetrics(): Promise<{ lessons: number; categories: number; progress: number }> {
    try {
      // Get categories count
      const categoriesQuery = query(
        collection(db, 'ai-devquest-categories')
      );
      const categoriesSnapshot = await getDocs(categoriesQuery);
      
      // Get lessons count
      const lessonsQuery = query(
        collection(db, 'ai-devquest-lessons')
      );
      const lessonsSnapshot = await getDocs(lessonsQuery);
      
      return {
        lessons: lessonsSnapshot.size,
        categories: categoriesSnapshot.size,
        progress: 0 // Default progress, would need user-specific data for actual progress
      };
    } catch (error) {
      
      return {
        lessons: 0, categories: 0, progress: 0
      };
    }
  },

  async getEmployeeDirectoryAppHubMetrics(): Promise<{ totalEmployees: number; totalDepartments: number; totalCountries: number; activeEmployees: number; lastActivity: string }> {
    try {
      const { employeeDirectoryMetricsCache } = await import('./employee-directory/employeeDirectoryMetricsCache');
      return await employeeDirectoryMetricsCache.getEmployeeDirectoryAppHubMetrics();
    } catch (error) {
      
      return {
        totalEmployees: 0,
        totalDepartments: 0,
        totalCountries: 0,
        activeEmployees: 0,
        lastActivity: '-'
      };
    }
  },

  async getProductManagerAppHubMetrics(): Promise<{ totalProducts: number; activeProducts: number; lastActivity: string }> {
    try {
      // Get cached Product Manager metrics from appHubMetrics collection
      const productManagerDoc = await firestoreWrapper.getDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('appHubMetrics'),
          'productmanager'
        )
      );
      
      if (!productManagerDoc.exists()) {
        return {
          totalProducts: 0,
          activeProducts: 0,
          lastActivity: '-'
        };
      }
      
      const data = productManagerDoc.data();
      
      // Format the last activity
      let lastActivity = '-';
      if (data.lastUpdated) {
        let date: Date;
        if (typeof data.lastUpdated === 'string') {
          date = new Date(data.lastUpdated);
        } else if (data.lastUpdated.toDate) {
          date = data.lastUpdated.toDate();
        } else {
          date = new Date(data.lastUpdated);
        }
        lastActivity = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
      }
      
      return {
        totalProducts: data.additionalData?.totalProducts || data.totalCount || 0,
        activeProducts: data.additionalData?.activeProducts || data.recentCount || 0,
        lastActivity: lastActivity
      };
    } catch (error) {
      return {
        totalProducts: 0,
        activeProducts: 0,
        lastActivity: '-'
      };
    }
  },

  clearCache(type?: string) {
    if (type) {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(`${GOOD_THINGS_CACHE_KEY}_${type}`)) {
          localStorage.removeItem(key);
        }
      });
    } else {
      this.clearAllCaches();
    }
  },

  clearAllCaches() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(GOOD_THINGS_CACHE_KEY)) {
        localStorage.removeItem(key);
      }
    });
    this.sortedEntriesCache.clear();
  }
};
